/**
 * What the brand can do with its budget in the escrow (specification §5.3): cancel the campaign
 * before its start and take the whole budget back. After settlement, withdraw the part the result
 * did not spend. Or, if the campaign was not settled before Day 10, withdraw the whole budget.
 *
 * The campaign is read from the escrow itself, so the actions offered are always current. A read
 * that fails says so, with a way to try again, rather than leaving the brand without its actions.
 * A cancellation, which gives the campaign up for good, is confirmed before the wallet is asked.
 * After an action that changes the campaign's status, the request is read again until Morcast
 * has recorded the new status from the chain, so the whole page shows it without a reload.
 */

import { campaignDayMs } from "@morcast/campaigns";
import { morcastEscrowAbi } from "@morcast/protocol";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Address } from "viem";
import { useConfig, useConnection, useReadContract } from "wagmi";

import { TransactionLink } from "../components/ChainLinks.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { SignInDialog } from "../components/SignInDialog.js";
import { SupportNote } from "../components/SupportNote.js";
import { Notice, styles } from "../components/ui.js";
import { formatAmount } from "../format.js";
import { callEscrow, describeEscrowError } from "../wallet/escrow.js";
import { type Campaign, type CampaignRequest, REQUESTS_KEY } from "./requests.js";

/** The escrow's campaign statuses, by their number in the contract. */
const FUNDED = 1;
const SETTLED = 3;

/** Day 10, from which an unsettled campaign's budget goes back to its brand, in seconds. */
export function refundAfterSeconds(): number {
  return (10 * campaignDayMs()) / 1000;
}

/** How often the request is read again while Morcast records an action, and for how long. */
const RECORDING_POLL_MS = 4_000;
const RECORDING_PATIENCE_MS = 2 * 60_000;

/** The escrow's record of a campaign, as far as the brand's actions depend on it. */
export interface FundsState {
  status: number;
  startAt: bigint;
  endAt: bigint;
  refund: bigint;
  refundPaid: boolean;
}

/**
 * What the brand can do at `now` (Unix seconds): cancel before the start, withdraw the refund of
 * a settled campaign, withdraw the budget of a campaign unsettled at Day 10, or nothing.
 */
export function fundsAction(state: FundsState, now: number): "cancel" | "refund" | "budget" | null {
  if (state.status === FUNDED && now < Number(state.startAt)) {
    return "cancel";
  }
  if (state.status === SETTLED && state.refund > 0n && !state.refundPaid) {
    return "refund";
  }
  if (state.status === FUNDED && now >= Number(state.endAt) + refundAfterSeconds()) {
    return "budget";
  }
  return null;
}

/**
 * The escrow's record of a campaign, read from the chain. Pages that show the same campaign share
 * one read.
 */
export function useEscrowCampaign(campaign: Pick<Campaign, "chainId" | "escrow" | "id">) {
  return useReadContract({
    chainId: campaign.chainId,
    address: campaign.escrow as Address,
    abi: morcastEscrowAbi,
    functionName: "getCampaign",
    args: [BigInt(campaign.id)],
  });
}

export function BrandFunds({
  campaign,
  token,
}: {
  campaign: Campaign;
  token: CampaignRequest["token"];
}) {
  const config = useConfig();
  const queryClient = useQueryClient();
  const { isConnected } = useConnection();
  const [confirming, setConfirming] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const target = { chainId: campaign.chainId, escrow: campaign.escrow as Address };
  const onChain = useEscrowCampaign(campaign);
  // The status the request shows once Morcast has recorded the brand's last action, if it changes
  // one: "cancelled" after a cancellation, "refunded" after withdrawing the whole budget.
  const [awaited, setAwaited] = useState<Campaign["status"] | null>(null);
  const act = useMutation({
    mutationFn: (input: {
      functionName: "cancel" | "withdrawBrand";
      awaits: Campaign["status"] | null;
    }) =>
      callEscrow(config, target, { functionName: input.functionName, args: [BigInt(campaign.id)] }),
    onSuccess: (_hash, input) => setAwaited(input.awaits),
    onSettled: () => {
      void onChain.refetch();
      void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
    },
  });

  // Until Morcast has recorded the new status, read the request again every few seconds.
  useEffect(() => {
    if (awaited === null) {
      return;
    }
    if (campaign.status === awaited) {
      setAwaited(null);
      return;
    }
    const poll = setInterval(
      () => void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
      RECORDING_POLL_MS,
    );
    const stop = setTimeout(() => setAwaited(null), RECORDING_PATIENCE_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(stop);
    };
  }, [awaited, campaign.status, queryClient]);

  if (onChain.isPending) {
    return (
      <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
        Reading the campaign from the escrow…
      </p>
    );
  }
  if (onChain.isError) {
    return (
      <div className="mt-4 text-sm">
        <Notice tone="error">
          The campaign could not be read from the escrow, so what you can do with its budget cannot
          be shown right now.
        </Notice>
        <button
          type="button"
          disabled={onChain.isFetching}
          onClick={() => void onChain.refetch()}
          className={`mt-3 ${styles.secondaryButton}`}
        >
          {onChain.isFetching ? "Reading…" : "Try again"}
        </button>
        <SupportNote className="mt-2" />
      </div>
    );
  }
  const state = onChain.data;
  const offered = fundsAction(state, Math.floor(Date.now() / 1000));
  const action =
    offered === "cancel"
      ? {
          functionName: "cancel" as const,
          awaits: "cancelled" as const,
          label: "Cancel the campaign",
          text: "Until the campaign starts, you can cancel it and take the whole budget back.",
        }
      : offered === "refund"
        ? {
            functionName: "withdrawBrand" as const,
            // The campaign stays settled. Only the escrow's record of the refund changes.
            awaits: null,
            label: `Withdraw ${formatAmount(state.refund.toString(), token)}`,
            text: "The part of the budget the result did not spend is yours to withdraw.",
          }
        : offered === "budget"
          ? {
              functionName: "withdrawBrand" as const,
              awaits: "refunded" as const,
              label: "Withdraw the budget",
              text: "The campaign was not settled before Day 10, so the whole budget is yours to withdraw.",
            }
          : null;

  return (
    <div className="mt-4 text-sm">
      {state.status === SETTLED && state.refundPaid && (
        <p className="text-zinc-700 dark:text-zinc-300">
          You withdrew the {formatAmount(state.refund.toString(), token)} the result did not spend.
        </p>
      )}
      {action && !act.isSuccess && (
        <>
          <p className="text-zinc-700 dark:text-zinc-300">{action.text}</p>
          {!isConnected ? (
            <>
              <Notice tone="info" className="mt-3">
                Your wallet is not connected. Sign in with it again to{" "}
                {action.functionName === "cancel" ? "cancel" : "withdraw"}.
              </Notice>
              <button
                type="button"
                onClick={() => setSigningIn(true)}
                className={`mt-3 ${styles.primaryButton}`}
              >
                Sign in
              </button>
              <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
            </>
          ) : (
            <button
              type="button"
              disabled={act.isPending}
              onClick={() =>
                action.functionName === "cancel"
                  ? setConfirming(true)
                  : act.mutate({ functionName: action.functionName, awaits: action.awaits })
              }
              className={`mt-3 ${action.functionName === "cancel" ? styles.dangerButton : styles.primaryButton}`}
            >
              {act.isPending ? "Confirm in your wallet…" : action.label}
            </button>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirming}
        title="Cancel the campaign?"
        confirmLabel="Cancel the campaign"
        cancelLabel="Keep the campaign"
        onConfirm={() => act.mutate({ functionName: "cancel", awaits: "cancelled" })}
        onClose={() => setConfirming(false)}
      >
        <p>
          The escrow returns the whole budget to your wallet and the campaign never starts. Creators
          cannot take part, and it cannot be funded again. To run it later, send a new request.
        </p>
      </ConfirmDialog>
      {act.data && (
        <p className="mt-2">
          Sent in transaction <TransactionLink chainId={campaign.chainId} hash={act.data} />
          {awaited !== null &&
            ". Morcast records it within a minute, and this page updates by itself."}
        </p>
      )}
      {act.isError && (
        <>
          <p role="alert" className="mt-2 text-red-700 dark:text-red-400">
            {describeEscrowError(act.error)}
          </p>
          <SupportNote className="mt-1" />
        </>
      )}
    </div>
  );
}
